import type { CustomerType } from '@/entities/User';

export interface ValidatePromoCodeParams {
  promoCode: string;
  isUpgrade: boolean;
}

export enum TransactionType {
  POL = 'POL', // new
  REN = 'REN', // renew
}

export enum PromoCodeLogic {
  REPLACE_EXISTING_DISCOUNT = 1,
  ADD_EXTRA_DISCOUNT = 2,
  REPLACE_WITH_NEW_TARIFF = 3,
}

export interface ValidatePromoCodeResponse {
  promoCode: {
    productType: string;
    transactionType: TransactionType;
    expirationTimestamp: number;
    discount: {
      value: number;
      name: string;
    };
    isAffinity: boolean;
    logic: PromoCodeLogic;
  };
  isValid: boolean;
}

export interface Benefit {
  id: string;
  name: string;
  description: string;
  price: number;
}

export interface Coverage {
  id: string;
  name: string;
  description: string;
  price?: number;
  checked?: boolean;
  benefits?: Benefit[];
  isGifted?: boolean;
  bonus?: number;
}

export interface TariffPrice {
  monthly: number;
  annual: number;
  deductibleValue: number;
  deductibleCode?: string;
}

export enum TariffType {
  COMPREHENSIVE = 'COMPREHENSIVE',
  COMPREHENSIVE_LITE = 'COMPREHENSIVE_LITE',
  TPL = 'TPL',
}

export interface Discount {
  premium: string;
  name: string;
}

export interface Tariff {
  id: string;
  name: string;
  type: TariffType;
  prices: TariffPrice;
  isGlmTariff: boolean;
  glmFlag: boolean;
  discount: number;
  discounts: Discount[];
  includedCovers: Coverage[];
  addons: Coverage[];
}

export interface QuoteState {
  quoteId: string;
  tariff: Tariff | null;
  tariffType: TariffType | null;
  promoCode: string;
  price: number | null;
  deductibleValue: number | null;
  policyStartDate: Date | null;
  productCode: ProductCode | null;
  productBonus: number | null;
}

export interface TariffsState {
  quotes: GetQuotesResponse | null;
  quoteError: Error | null;
}

export interface Quote {
  quoteId: string;
  tariffs: Tariff[];
  productBonus: number | null;
}

export type PageCode = 'plans' | 'select-comprehensive-plan' | 'quote' | 'quote_od' | 'quote_tpl';
export type ProductType = 'car' | 'car_od' | 'car_tpl';

export interface SaveQuotesRequest {
  clientState?: string; // Additional data, JSON string with client state cache
  email?: string;
  googleClientId?: string; // Google client id, Cut by rules: remove “GAX.X” prefix.
  // E.g. if _ga=GA1.1.1297241831.1677406492 then remove GA1.1. and send only 1297241831.1677406492
  language: 'en' | 'ar';
  pageCode: PageCode;
  phone: string;
  priceComprehensive?: string;
  priceTPL?: string;
  productType: ProductType;
  quotes: string[]; // list of quote identifiers
  seats?: number; // Number of seats in the vehicle.
  testMode?: boolean; // Optional parameter to enable test mode.
  vehicleValue?: number; // Vehicle value in QAR.
}

export interface GetQuotesParams {
  vehicleValue: number;
  promoCode?: string;
  [key: string]: unknown;
}

export interface CoreQuoteRequestParams {
  carInfo: {
    makeId: string;
    cylindersCount: number;
    modelId: string;
    modelYear: number;
    registrationYear?: number;
    seatsCount: number;
    vehicleValue: number;
    vehicleTypeId: string;
    vehicleUsageName: 'PRIVATE' | 'COMMERCIAL';
    vehicleShapeId: string;
    vin?: string;
  };
  clientInfo: {
    userId: string;
  };
  customerType: CustomerType;
  promoCode?: string;
}

export interface GetQuotesResponse {
  tpl: Quote | null;
  comprehensive: Quote | null;
}

export interface SaveQuotesResponse {
  userStepGuid?: string;
}

export interface GetSavedQuotesParams {
  userStepGuid: string;
}

export interface SavedQuotesResponse {
  retrieveOffer: {
    time: number;
    price: number;
    isActive: boolean;
  };
  user: {
    dateOfBirth: string;
    geoArea: string;
    phone: string;
    userId: string;
    fullName: string;
    email: string;
  };
  policy: {
    dateFrom: string;
    dateTo: string;
    year: number;
    previousInsuranceValid: string;
  };
  car: {
    plateNumber: string;
    plateTypeId: string;
    vehicleTypeId: string;
    vehicleUsageId: string;
    vehicleShapeId: string;
    seatsId: string;
    cylindersId: string;
    carValue: number;
    modelId: string;
    modelYear: number;
    age: number;
    registrationYear: number;
    vin: string;
  };
  quotes: {
    tpl: Quote | null;
    comprehensive: Quote | null;
  };
  additionalData: string;
}

export enum ProductCode {
  TPL = '0100',
  COMPREHENSIVE = '0110',
  COMPREHENSIVE_LITE = '', // The user cannot reach the checkout page with COMPREHENSIVE LITE selected
}

export interface AddonsState {
  selectedAddons: Coverage[];
  cyberWasUnselected: boolean;
}
